import { Loader2 } from "lucide-react"
import * as React from "react"
import { useNavigate, useSearchParams } from "react-router"

import { NewPasswordFields } from "@/components/domain/new-password-fields"
import { newPasswordReady } from "@/components/domain/password-rules"
import { Alert } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { FormField } from "@/components/ui/form-field"
import { Input } from "@/components/ui/input"

import { AuthLayout } from "./auth-layout"
import { useAcceptInviteMutation, useCurrentUser, type AcceptInviteError } from "./auth-store"

export function AcceptInvitePage(): JSX.Element {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get("token")

  const accept = useAcceptInviteMutation()
  const currentUser = useCurrentUser()

  // A signed-in user who opens someone else's invite must NOT be silently redirected past the
  // acceptance form — the invitation would go unspent. Only bounce if there is no token to
  // accept (the page was opened without one).
  React.useEffect(() => {
    if (currentUser && !token) navigate("/reviews", { replace: true })
  }, [currentUser, token, navigate])

  const [fullName, setFullName] = React.useState("")
  const [password, setPassword] = React.useState("")
  const [confirm, setConfirm] = React.useState("")
  const ready = newPasswordReady(password, confirm)

  if (!token) {
    return (
      <AuthLayout
        eyebrow="Invitation"
        title="Invitation link missing"
        subtitle="This page expects an invitation token in the URL."
      >
        <Alert tone="error" size="sm" title="No invitation token">
          Ask your Pyxis admin to resend the invitation.
        </Alert>
      </AuthLayout>
    )
  }

  const onSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault()
    if (!ready || accept.isPending) return
    try {
      await accept.mutateAsync({ token, newPassword: password, fullName: fullName || null })
      navigate("/reviews", { replace: true })
    } catch {
      /* surfaced via accept.error */
    }
  }

  const topError = toTopError(accept.error)
  const weakPassword = accept.error?.kind === "weak_password" ? accept.error.message : null
  const edit = (setter: (value: string) => void) => (value: string) => {
    setter(value)
    // Only a refusal this edit can fix. A dead token cannot be typed away, and dismissing its
    // alert would hide the one thing telling them to ask for a new invitation.
    if (accept.error?.kind === "weak_password") accept.reset()
  }

  return (
    <AuthLayout
      eyebrow="Invitation"
      title="Set your password"
      subtitle="You've been invited to PyxisRC Label Review. Choose a password to activate your account."
    >
      <form
        onSubmit={onSubmit}
        className="space-y-4"
        noValidate
        aria-busy={accept.isPending || undefined}
      >
        <div className="sr-only" aria-live="polite" role="status">
          {accept.isPending ? "Setting your password…" : topError ? topError.body : ""}
        </div>

        {topError && (
          <Alert tone="error" size="sm" title={topError.title}>
            {topError.body}
          </Alert>
        )}

        {currentUser && (
          <Alert tone="warning" size="sm" title={`Signed in as ${currentUser.email}`}>
            Setting a password here will replace your current session with the invitee's account.
          </Alert>
        )}

        <FormField label="Full name" hint="Optional — shown to teammates on comments and reviews.">
          <Input
            type="text"
            autoComplete="name"
            inputSize="lg"
            value={fullName}
            onChange={(e) => setFullName(e.currentTarget.value)}
            disabled={accept.isPending}
            autoFocus
          />
        </FormField>

        <NewPasswordFields
          label="Password"
          password={password}
          onPasswordChange={edit(setPassword)}
          confirmation={confirm}
          onConfirmationChange={edit(setConfirm)}
          serverError={weakPassword}
          inputSize="lg"
          disabled={accept.isPending}
        />

        <Button
          type="submit"
          variant="primary"
          size="lg"
          block
          leadingIcon={accept.isPending ? <Loader2 className="animate-spin" /> : undefined}
          disabled={!ready || accept.isPending}
        >
          {accept.isPending ? "Setting up…" : "Set password and sign in"}
        </Button>
      </form>
    </AuthLayout>
  )
}

interface TopError {
  title: string
  body: string
}

function toTopError(error: AcceptInviteError | null): TopError | null {
  if (!error) return null
  switch (error.kind) {
    case "weak_password":
      // Shown on the password field, where the fix is.
      return null
    case "invalid_or_expired":
      return {
        title: "Invitation invalid or expired",
        body: "Ask your Pyxis admin to resend the invitation.",
      }
    case "network":
      return {
        title: "Network problem",
        body: "We couldn't reach the server. Check your connection and try again.",
      }
    case "server":
      return {
        title: "Server error",
        body: "Something went wrong on our side. Please try again in a moment.",
      }
    default:
      return { title: "Could not accept invitation", body: error.message }
  }
}
